import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Tema de la aplicacion.
 *
 * Hay dos conceptos distintos y conviene no mezclarlos:
 *   - `preference`: lo que el usuario eligio. Puede ser "seguir al sistema".
 *   - `theme`: el tema que efectivamente se esta pintando, siempre claro u
 *     oscuro.
 *
 * El atributo `data-theme` del <html> es la unica salida: toda la hoja de
 * estilos cuelga de el. El primer valor lo pone un script en index.html antes
 * del primer pintado, de modo que aqui solo se sincroniza, nunca se inicia
 * desde cero (si no, habria un destello claro en cada recarga).
 */

const STORAGE_KEY = 'nisaguard-theme';

const ThemeContext = createContext(null);

const systemTheme = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

const readPreference = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch (e) {
    /* almacenamiento bloqueado: se sigue con la preferencia del sistema */
  }
  return 'system';
};

/**
 * Aplica el tema al documento.
 *
 * Con View Transitions el cambio es un fundido de toda la pagina en lugar de
 * un salto seco, que es lo unico que justifica animar aqui. Donde no existe la
 * API, o cuando el usuario pide menos movimiento, el cambio es instantaneo.
 */
const applyTheme = (theme, animate) => {
  const root = document.documentElement;

  if (root.getAttribute('data-theme') === theme) return;

  const commit = () => root.setAttribute('data-theme', theme);

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Las transiciones por elemento se apagan durante el cambio.
     Sin esto, cada boton, tarjeta y borde con `transition: background-color`
     arranca su propia interpolacion en el mismo instante en que cambian las
     variables, y varias se quedan congeladas en el color anterior hasta la
     siguiente recarga. El fundido global ya lo hace la View Transition, que
     ademas es una sola animacion en lugar de doscientas. */
  root.setAttribute('data-theme-switching', '');
  const release = () => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => root.removeAttribute('data-theme-switching'))
    );
  };

  if (animate && !reduced && typeof document.startViewTransition === 'function') {
    try {
      // La transicion puede abortarse (pestana oculta, otra en curso). No es un
      // fallo: el cambio de tema ya se aplico, solo se pierde el fundido. Se
      // silencian las tres promesas, no solo `finished`, porque `ready` es la
      // que rechaza cuando la transicion se descarta.
      const vt = document.startViewTransition(commit);
      const hush = () => {};
      vt.ready.catch(hush);
      vt.updateCallbackDone.catch(hush);
      vt.finished.then(release, () => { release(); }).catch(hush);
      return;
    } catch (e) {
      /* sin transicion, cambio directo */
    }
  }

  commit();
  release();
};

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(readPreference);
  const [resolved, setResolved] = useState(() =>
    typeof document !== 'undefined'
      ? document.documentElement.getAttribute('data-theme') || systemTheme()
      : 'light'
  );

  // Sigue al sistema mientras el usuario no haya elegido explicitamente.
  useEffect(() => {
    if (preference !== 'system') {
      setResolved(preference);
      return undefined;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = () => setResolved(media.matches ? 'dark' : 'light');
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, [preference]);

  // Primer render: el atributo ya viene puesto por index.html, asi que solo se
  // anima a partir del segundo cambio. Se guarda en una ref y no en estado para
  // que montar no dispare una transicion de mas.
  const firstRun = useRef(true);
  useEffect(() => {
    applyTheme(resolved, !firstRun.current);
    firstRun.current = false;
  }, [resolved]);

  const choose = useCallback((next) => {
    setPreference(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* sin almacenamiento la eleccion dura lo que la sesion */
    }
  }, []);

  const toggle = useCallback(() => {
    choose(resolved === 'dark' ? 'light' : 'dark');
  }, [choose, resolved]);

  const value = useMemo(
    () => ({ theme: resolved, preference, setPreference: choose, toggle }),
    [resolved, preference, choose, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  }
  return ctx;
}

export default ThemeContext;
